import React, { useState, useEffect, useMemo, useRef } from 'react';
import { initializeApp } from 'firebase/app';
import { 
  getFirestore, 
  collection, 
  addDoc, 
  onSnapshot, 
  query, 
  deleteDoc, 
  doc, 
  serverTimestamp,
  updateDoc,
  getDoc,
  setDoc
} from 'firebase/firestore';
import { 
  getAuth, 
  signInAnonymously, 
  signInWithCustomToken, 
  onAuthStateChanged 
} from 'firebase/auth';
import { 
  PlusCircle, 
  TrendingUp, 
  TrendingDown, 
  Trash2, 
  Edit3, 
  Wallet, 
  Search, 
  Filter, 
  Store, 
  Briefcase, 
  Coins, 
  FileText, 
  ChevronDown, 
  AlertCircle, 
  ArrowRightLeft, 
  Split, 
  Plus, 
  Minus, 
  Zap, 
  Monitor, 
  X, 
  Download, 
  Copy, 
  CreditCard,
  CheckSquare,
  Square,
  AlertTriangle,
  Info,
  Terminal,
  Settings,
  Tag
} from 'lucide-react';

// --- Konfigurasi Firebase Milik User ---
const firebaseConfig = {
  apiKey: "AIzaSyB1HyhOCCTa8Cktu4DgGciA2EbVel_zAgU",
  authDomain: "finance-manager-855.firebaseapp.com",
  projectId: "finance-manager-855",
  storageBucket: "finance-manager-855.firebasestorage.app",
  messagingSenderId: "707649134387",
  appId: "1:707649134387:web:12f384e57137de504d2846"
};

// Inisialisasi Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);
const appId = "finance-manager-855";

// --- Data Master Statis ---
const LIST_DIVISI = ["SEO", "CRM", "TOKO", "PROJECT", "DESIGN", "HRD"];
const LIST_SUMBER_DANA = ["SEO", "HRD"]; 
const LIST_STATUS = ["SUDAH CLAIM", "BELUM CLAIM", "SUDAH KEMBALIKAN", "BELUM KEMBALIKAN", "CICIL", "DONE"];

const MONTHS = [
  { val: 'ALL', label: '--- ALL_MONTHS ---' },
  { val: 0, label: 'JANUARY' }, { val: 1, label: 'FEBRUARY' }, { val: 2, label: 'MARCH' },
  { val: 3, label: 'APRIL' }, { val: 4, label: 'MAY' }, { val: 5, label: 'JUNE' },
  { val: 6, label: 'JULY' }, { val: 7, label: 'AUGUST' }, { val: 8, label: 'SEPTEMBER' },
  { val: 9, label: 'OCTOBER' }, { val: 10, label: 'NOVEMBER' }, { val: 11, label: 'DECEMBER' }
];

const ITEMS_PER_PAGE = 10;

// Helper: Format Mata Uang Aman
const safeFormatIDR = (val) => {
  const num = Number(val) || 0;
  return `Rp${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const parseNominal = (val) => {
  if (typeof val !== 'string') val = String(val);
  const cleaned = val.replace(/,/g, '');
  return parseFloat(cleaned);
};

const getStatusColor = (status) => {
  const s = String(status || '').toUpperCase();
  if (s === 'DONE' || s === 'SUDAH KEMBALIKAN') return 'bg-green-50 text-green-800 border-green-400';
  if (s === 'SUDAH CLAIM') return 'bg-blue-50 text-blue-800 border-blue-400';
  if (s === 'BELUM KEMBALIKAN' || s === 'BELUM CLAIM') return 'bg-red-50 text-red-800 border-red-400';
  if (s === 'CICIL') return 'bg-orange-50 text-orange-800 border-orange-400';
  return 'bg-white text-slate-700 border-slate-300';
};

// --- Matrix Rain Background Animation ---
const MatrixBackground = () => {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    const chars = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ$+-*/=%';
    const fontSize = 14;
    const columns = canvas.width / fontSize;
    const drops = Array(Math.floor(columns)).fill(1);
    const draw = () => {
      ctx.fillStyle = 'rgba(0, 0, 0, 0.05)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = '#0F0';
      ctx.font = fontSize + 'px monospace';
      for (let i = 0; i < drops.length; i++) {
        const text = chars.charAt(Math.floor(Math.random() * chars.length));
        ctx.fillText(text, i * fontSize, drops[i] * fontSize);
        if (drops[i] * fontSize > canvas.height && Math.random() > 0.975) drops[i] = 0;
        drops[i]++;
      }
    };
    const interval = setInterval(draw, 33);
    return () => clearInterval(interval);
  }, []);
  return <canvas ref={canvasRef} className="fixed inset-0 z-0 opacity-20 pointer-events-none" />;
};

// --- BIOS Loading Component ---
const BiosLoading = ({ status, error }) => {
  const [lines, setLines] = useState([]);
  const fullLog = useMemo(() => [
    "BIOS Revision 855-X.001",
    "Checking System RAM ................. 65536MB OK",
    "Detecting CPU ....................... AI Quantum V8 @ 5.4GHz",
    "CMOS Battery ........................ Voltage Normal",
    "Handshaking with finance-manager-855.firebaseapp.com",
    "Protocol: WSS Secure Link Enabled",
    "Loading Database Modules ............ 100%",
    "System Kernel ....................... Up and running",
    ">>> FINANCE_EXECUTIVE_OS_LOADED <<<"
  ], []);

  useEffect(() => {
    let currentLine = 0;
    const interval = setInterval(() => {
      if (currentLine < fullLog.length) {
        setLines(prev => [...prev, fullLog[currentLine]]);
        currentLine++;
      } else { clearInterval(interval); }
    }, 150);
    return () => clearInterval(interval);
  }, [fullLog]);

  return (
    <div className="min-h-screen bg-black text-green-500 font-mono p-4 md:p-10 text-xs md:text-sm leading-relaxed overflow-hidden relative">
      <MatrixBackground />
      <div className="max-w-4xl mx-auto relative z-10">
        <pre className="text-red-600 font-bold mb-8 text-[8px] md:text-[10px] leading-tight animate-pulse">
{`
 ######  #  #######  #####  #######    #    ######  
 #     # #  #     # #     #    #      # #   #     # 
 #     # #  #     # #          #     #   #  #     # 
 ######  #  #     #  #####     #    #     # ######  
 #     # #  #     #       #    #    ####### #   #   
 #     # #  #     # #     #    #    #     # #    #  
 ######  #  #######  #####     #    #     # #     # 
`}
        </pre>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="space-y-0.5">
            {lines.map((line, idx) => (
              <p key={`log-${idx}`} className="flex gap-2">
                <span className="text-green-800">[{new Date().toLocaleTimeString()}]</span>
                <span>{String(line)}</span>
              </p>
            ))}
          </div>
        </div>
        <div className="mt-8 pt-4 border-t border-green-900 flex flex-col gap-2">
          <div className="flex items-center gap-3">
            <Terminal size={16} className="animate-bounce" />
            <p className={`${error ? 'text-red-500' : 'text-white'} font-black text-sm uppercase tracking-widest`}>
              {error ? 'FATAL_ERROR: ' + String(error) : 'STATUS: ' + String(status)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- MAIN APP COMPONENT ---
export default function App() {
  const [user, setUser] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [stores, setStores] = useState([]); 
  const [accounts, setAccounts] = useState({
    SEO: { owner: '', number: '', bank: '' },
    HRD: { owner: '', number: '', bank: '' }
  });
  
  const [loading, setLoading] = useState(true);
  const [initStatus, setInitStatus] = useState("Initializing System...");
  const [initError, setInitError] = useState(null);
  
  // States for Filters
  const [filterSource, setFilterSource] = useState('SEMUA');
  const [filterToko, setFilterToko] = useState('SEMUA');
  const [filterYear, setFilterYear] = useState(new Date().getFullYear().toString());
  const [filterMonth, setFilterMonth] = useState('ALL');
  const [searchTerm, setSearchTerm] = useState('');

  const [visibleCount, setVisibleCount] = useState(ITEMS_PER_PAGE);
  const [selectedIds, setSelectedIds] = useState([]);
  const [isSplitMode, setIsSplitMode] = useState(false);
  
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [isAccountModalOpen, setIsAccountModalOpen] = useState(false);
  const [accountToEdit, setAccountToEdit] = useState(null);
  const [isStoreMgmtModalOpen, setIsStoreMgmtModalOpen] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [messageBox, setMessageBox] = useState(null);
  const [storeToEdit, setStoreToEdit] = useState(null);

  const [formData, setFormData] = useState({
    amount: '', description: '', notes: '', type: 'EXPENSE',
    toko: '', division: 'SEO', source: 'SEO', status: 'BELUM CLAIM'
  });

  const [splitData, setSplitData] = useState({
    totalAmount: '', totalItems: '', rows: []
  });

  const showAlert = (title, msg, type = 'info') => setMessageBox({ title, msg, type });

  // 1. Firebase Auth
  useEffect(() => {
    const initAuth = async () => {
      try {
        setInitStatus("Handshaking with Firebase...");
        const customToken = typeof window !== 'undefined' ? window.__initial_auth_token : undefined;
        if (customToken) {
          try { await signInWithCustomToken(auth, customToken); } 
          catch (e) { await signInAnonymously(auth); }
        } else { await signInAnonymously(auth); }
        setInitStatus("Identity Verified. Requesting Data Streams...");
      } catch (err) { 
        setInitError(`Authentication Failure: ${err.message}`);
        setLoading(false);
      }
    };
    initAuth();
    const unsubscribe = onAuthStateChanged(auth, (u) => {
      setUser(u);
      if (!u) setTimeout(() => { if(!auth.currentUser) setLoading(false); }, 10000);
    });
    return () => unsubscribe();
  }, []);

  // 2. Firebase Sync
  useEffect(() => {
    if (!user) return;
    setInitStatus("Synchronizing Global Ledger State...");
    
    const unsubAcc = onSnapshot(doc(db, 'artifacts', appId, 'public', 'data', 'settings', 'accounts'), (ds) => {
      if (ds.exists()) setAccounts(ds.data());
    });

    const unsubStores = onSnapshot(doc(db, 'artifacts', appId, 'public', 'data', 'settings', 'stores'), (ds) => {
      if (ds.exists()) {
        const list = ds.data().list || [];
        setStores(list);
        if (list.length > 0 && !formData.toko) {
          const firstStore = list[0];
          setFormData(prev => ({...prev, toko: firstStore?.name || ''}));
        }
      }
    });

    const unsubTx = onSnapshot(query(collection(db, 'artifacts', appId, 'public', 'data', 'cashflow')), (snapshot) => {
      const data = snapshot.docs.map(item => ({ id: String(item.id), ...item.data() }));
      setTransactions(data.sort((a, b) => (Number(b.createdAt?.seconds) || 0) - (Number(a.createdAt?.seconds) || 0)));
      setTimeout(() => setLoading(false), 2000);
    }, (err) => {
      setInitError(`Database Error: ${err.message}`);
      setLoading(false);
    });

    return () => { unsubAcc(); unsubStores(); unsubTx(); };
  }, [user, formData.toko]);

  // --- Handlers ---
  const handleAddStore = async (name, category) => {
    if (!name) return;
    const cleanName = name.trim().toUpperCase();
    if (stores.some(s => s && s.name === cleanName)) return showAlert("ERROR", "STORE_EXISTS", "error");
    try {
      const storesRef = doc(db, 'artifacts', appId, 'public', 'data', 'settings', 'stores');
      await setDoc(storesRef, { list: [...stores, { name: cleanName, category }] });
      showAlert("SUCCESS", "STORE_ADDED");
    } catch (err) { showAlert("DB_ERROR", err.message, "error"); }
  };

  const handleUpdateStore = async (oldName, newName, newCategory) => {
    if (!newName) return;
    const cleanName = newName.trim().toUpperCase();
    const newList = stores.map(s => s && s.name === oldName ? { name: cleanName, category: newCategory } : s);
    try {
      const storesRef = doc(db, 'artifacts', appId, 'public', 'data', 'settings', 'stores');
      await setDoc(storesRef, { list: newList });
      setStoreToEdit(null);
      showAlert("SUCCESS", "STORE_UPDATED");
    } catch (err) { showAlert("DB_ERROR", err.message, "error"); }
  };

  const handleDeleteStore = async (name) => {
    const newList = stores.filter(s => s && s.name !== name);
    try {
      const storesRef = doc(db, 'artifacts', appId, 'public', 'data', 'settings', 'stores');
      await setDoc(storesRef, { list: newList });
      showAlert("SUCCESS", "STORE_REMOVED");
    } catch (err) { showAlert("DB_ERROR", err.message, "error"); }
  };

  const handleAddSingle = async (ev) => {
    ev.preventDefault();
    if (!user || !formData.toko) return showAlert("ERROR", "MISSING_STORE", "error");
    const parsedAmount = parseNominal(formData.amount);
    if (isNaN(parsedAmount)) return showAlert("ERROR", "INVALID_VAL", "error");
    try {
      await addDoc(collection(db, 'artifacts', appId, 'public', 'data', 'cashflow'), {
        ...formData, amount: parsedAmount, userId: user.uid, createdAt: serverTimestamp()
      });
      setFormData(prev => ({ ...prev, amount: '', description: '', notes: '' }));
      showAlert("SUCCESS", "SAVED");
    } catch (err) { showAlert("DB_ERROR", err.message, "error"); }
  };

  const handleAutoSplit = () => {
    const mainBrands = stores.filter(s => s && s.category === 'MAIN');
    if (mainBrands.length === 0) return showAlert("ERROR", "NO_MAIN_STORES", "error");
    setSplitData({
      ...splitData,
      totalItems: mainBrands.length.toString(),
      rows: mainBrands.map(s => ({ toko: s.name, itemCount: '1' }))
    });
  };

  const handleAddSplit = async (ev) => {
    ev.preventDefault();
    const totalAmt = parseNominal(splitData.totalAmount);
    const totalQty = parseFloat(splitData.totalItems);
    if (isNaN(totalAmt) || isNaN(totalQty) || totalQty <= 0) return showAlert("ERROR", "INVALID_SPLIT", "error");
    const pricePerItem = totalAmt / totalQty;
    try {
      for (const row of splitData.rows) {
        if (!row.itemCount || parseFloat(row.itemCount) <= 0) continue;
        await addDoc(collection(db, 'artifacts', appId, 'public', 'data', 'cashflow'), {
          description: `${formData.description} (Split)`, notes: formData.notes, amount: Number(pricePerItem * parseFloat(row.itemCount)), 
          type: formData.type, toko: String(row.toko), division: formData.division, source: formData.source, 
          status: formData.status, userId: user.uid, createdAt: serverTimestamp()
        });
      }
      setIsSplitMode(false);
      showAlert("SUCCESS", "BATCH_SAVED");
    } catch (err) { showAlert("DB_ERROR", err.message, "error"); }
  };

  const handleCopySelected = () => {
    if (selectedIds.length === 0) return;
    const text = transactions.filter(t => selectedIds.includes(t.id)).map(item => {
      const date = item.createdAt?.seconds ? new Date(item.createdAt.seconds * 1000).toLocaleDateString('id-ID') : 'PENDING';
      const acc = accounts[item.source] || { owner: '?', number: '?', bank: '?' };
      return `(${date}), ${item.description}\n${item.toko} = ${safeFormatIDR(item.amount)}\n${acc.bank} - ${acc.number} a/n ${acc.owner}`;
    }).join('\n\n---\n\n');
    const textArea = document.createElement("textarea");
    textArea.value = text;
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand('copy');
    document.body.removeChild(textArea);
    showAlert("CLIPBOARD", "COPIED");
  };

  const exportToCSV = () => {
    const headers = ["Date", "Description", "Notes", "Store", "Division", "Source", "Type", "Amount", "Status"];
    const rows = filteredList.map(t => {
      const dateStr = t.createdAt?.seconds ? new Date(t.createdAt.seconds * 1000).toLocaleString() : 'N/A';
      return [`"${dateStr}"`, `"${t.description}"`, `"${t.notes || ''}"`, `"${String(t.toko)}"`, `"${t.division}"`, `"${t.source}"`, `"${t.type}"`, t.amount, `"${t.status}"`];
    });
    const csvContent = [headers, ...rows].map(e => e.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `FINANCE_EXPORT.csv`;
    link.click();
  };

  const updateStatus = async (id, newStatus) => {
    try { await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'cashflow', id), { status: String(newStatus) }); } 
    catch (e) { console.error(e); }
  };

  const handleUpdateAccount = async (ev) => {
    ev.preventDefault();
    try {
      await setDoc(doc(db, 'artifacts', appId, 'public', 'data', 'settings', 'accounts'), accounts);
      setIsAccountModalOpen(false);
      showAlert("SUCCESS", "ACCOUNT_UPDATED");
    } catch (err) { showAlert("ERROR", err.message, "error"); }
  };

  const handleUpdateTransaction = async (ev) => {
    ev.preventDefault();
    const amt = parseNominal(editItem.amount);
    if (isNaN(amt)) return;
    try {
      await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'cashflow', editItem.id), { ...editItem, amount: amt });
      setIsEditModalOpen(false);
      showAlert("SUCCESS", "RECORD_UPDATED");
    } catch (e) { showAlert("ERROR", e.message, "error"); }
  };

  const executeDelete = async () => {
    try {
      await deleteDoc(doc(db, 'artifacts', appId, 'public', 'data', 'cashflow', confirmDeleteId));
      setConfirmDeleteId(null);
      showAlert("INFO", "DELETED");
    } catch (e) { showAlert("ERROR", e.message, "error"); }
  };

  const toggleSelect = (id) => setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);

  const handleToggleSelectAll = () => {
    if (isAllSelected) {
      const ids = filteredList.map(i => i.id);
      setSelectedIds(prev => prev.filter(id => !ids.includes(id)));
    } else {
      const newIds = filteredList.map(i => i.id);
      setSelectedIds(prev => [...new Set([...prev, ...newIds])]);
    }
  };

  // --- Filter & Memos ---
  const filteredList = useMemo(() => {
    return transactions.filter(t => {
      const date = t.createdAt?.seconds ? new Date(t.createdAt.seconds * 1000) : null;
      
      // Filter by Category (HRD/SEO)
      const matchSource = filterSource === 'SEMUA' || t.source === filterSource;
      
      // Filter by Store
      const matchToko = filterToko === 'SEMUA' || String(t.toko) === filterToko;
      
      // Filter by Search Term
      const matchSearch = searchTerm === '' || 
        t.description?.toLowerCase().includes(searchTerm.toLowerCase()) || 
        (t.notes || '').toLowerCase().includes(searchTerm.toLowerCase());
      
      // Date-based Filters
      if (!date) {
        return (filterYear === 'ALL' && filterMonth === 'ALL') && matchSource && matchToko && matchSearch;
      }
      
      const matchYear = filterYear === 'ALL' || date.getFullYear().toString() === filterYear;
      const matchMonth = filterMonth === 'ALL' || date.getMonth().toString() === filterMonth;
      
      return matchSource && matchToko && matchSearch && matchYear && matchMonth;
    });
  }, [transactions, filterSource, filterToko, searchTerm, filterYear, filterMonth]);

  const isAllSelected = useMemo(() => filteredList.length > 0 && filteredList.every(item => selectedIds.includes(item.id)), [filteredList, selectedIds]);

  const stats = useMemo(() => {
    const calc = (s) => filteredList.filter(t => t.source === s).reduce((acc, curr) => (curr.type === 'INCOME' ? acc + curr.amount : acc - curr.amount), 0);
    return { 
      total: filteredList.reduce((acc, curr) => (curr.type === 'INCOME' ? acc + curr.amount : acc - curr.amount), 0), 
      seo: calc('SEO'), 
      hrd: calc('HRD'), 
      unpaid: filteredList.filter(t => String(t.status || '').includes('BELUM')).length 
    };
  }, [filteredList]);

  const tokoSummary = useMemo(() => {
    const summary = {};
    stores.forEach(toko => {
      if (!toko || !toko.name) return;
      const tokoTx = filteredList.filter(t => String(t.toko) === toko.name);
      summary[toko.name] = {
        balanceSEO: tokoTx.filter(t => t.source === "SEO").reduce((acc, curr) => (curr.type === 'INCOME' ? acc + curr.amount : acc - curr.amount), 0),
        balanceHRD: tokoTx.filter(t => t.source === "HRD").reduce((acc, curr) => (curr.type === 'INCOME' ? acc + curr.amount : acc - curr.amount), 0),
        category: toko.category
      };
    });
    return summary;
  }, [filteredList, stores]);

  if (loading) return <BiosLoading status={initStatus} error={initError} />;

  return (
    <div className="min-h-screen bg-[#d4d0c8] font-mono p-4 text-sm leading-tight select-none">
      <div className="max-w-7xl mx-auto">
        {/* Header Bar Win98 */}
        <div className="bg-[#000080] text-white p-2 px-3 mb-6 flex items-center justify-between shadow-[inset_-1px_-1px_#000,inset_1px_1px_#fff]">
          <div className="flex items-center gap-3 font-bold text-lg"><Monitor size={20} /><span>FINANCE_STATION_855_V12.EXE</span></div>
          <div className="flex gap-1">
            <div className="w-6 h-6 bg-[#d4d0c8] border border-black text-black flex items-center justify-center text-xs shadow-sm cursor-pointer hover:bg-white">_</div>
            <div className="w-6 h-6 bg-[#d4d0c8] border border-black text-black flex items-center justify-center text-sm shadow-sm cursor-pointer hover:bg-white">□</div>
            <div className="w-6 h-6 bg-[#d4d0c8] border border-black text-black flex items-center justify-center text-xs shadow-sm hover:bg-red-600 hover:text-white font-bold cursor-pointer">X</div>
          </div>
        </div>

        {/* Global Statistics Win98 Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          {[
            { label: 'GLOBAL_LEDGER', val: safeFormatIDR(stats.total), color: stats.total >= 0 ? 'text-black' : 'text-red-700' },
            { label: 'SEO_RESERVE', val: safeFormatIDR(stats.seo), color: 'text-blue-900' },
            { label: 'HRD_RESERVE', val: safeFormatIDR(stats.hrd), color: 'text-purple-900' },
            { label: 'ACTION_ITEMS', val: `${stats.unpaid} RECORDS`, color: 'text-orange-900' }
          ].map((s, i) => (
            <div key={`stat-${i}`} className="bg-[#d4d0c8] border-2 border-white border-r-black border-b-black p-4 shadow-sm">
              <span className="text-[11px] font-black block mb-2 uppercase text-[#505050] underline decoration-blue-800">{s.label}</span>
              <h3 className={`text-base font-black ${s.color}`}>{s.val}</h3>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Left Panel Win98 */}
          <div className="lg:col-span-4 space-y-6">
            <div className="bg-[#d4d0c8] border-2 border-white border-r-black border-b-black p-5 shadow-md">
              <div className="flex items-center justify-between mb-4 border-b-2 border-[#808080] pb-2 text-[#000080] uppercase tracking-tighter underline">
                <h2 className="text-base font-black flex items-center gap-2 font-bold"><CreditCard size={18} /> Registry</h2>
                <button onClick={() => setIsStoreMgmtModalOpen(true)} className="bg-[#d4d0c8] border-2 border-white border-r-black border-b-black px-2 py-1 text-[10px] font-black flex items-center gap-1 hover:bg-white"><Settings size={12}/> MANAGE_STORES</button>
              </div>
              <div className="space-y-4">
                {LIST_SUMBER_DANA.map(fund => (
                  <div key={`fund-info-${fund}`} className="bg-white border-2 border-gray-400 border-r-white border-b-white p-4 relative group shadow-inner">
                    <button onClick={() => { setAccountToEdit(fund); setIsAccountModalOpen(true); }} className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 bg-[#d4d0c8] border border-black p-1 transition-all"><Edit3 size={14} /></button>
                    <p className="text-[11px] font-black text-blue-900 underline uppercase tracking-tighter font-mono">SOURCE_{fund}:</p>
                    <p className="text-xs font-bold text-gray-700 uppercase">Owner: {accounts[fund].owner || 'N/A'}</p>
                    <p className="text-xs font-bold text-gray-700 uppercase">Bank: {accounts[fund].bank || 'N/A'}</p>
                    <p className="text-sm font-black tracking-widest text-red-800"># {accounts[fund].number || '---'}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-[#d4d0c8] border-2 border-white border-r-black border-b-black p-5 shadow-md font-mono">
              <div className="flex items-center justify-between mb-5 border-b-2 border-[#808080] pb-3"><h2 className="text-base font-black flex items-center gap-2 underline uppercase tracking-tighter">Data_Commit</h2><button onClick={() => setIsSplitMode(!isSplitMode)} className={`px-4 py-1 text-[11px] font-black border-2 ${isSplitMode ? 'bg-[#808080] text-white' : 'bg-[#d4d0c8]'}`}>{isSplitMode ? 'SPLIT:ON' : 'SPLIT:OFF'}</button></div>
              <form onSubmit={isSplitMode ? handleAddSplit : handleAddSingle} className="space-y-4">
                <div className="grid grid-cols-2 gap-2 bg-[#c0c0c0] p-2 border border-inset border-[#808080]">
                  <button type="button" onClick={() => setFormData({...formData, type: 'EXPENSE'})} className={`py-2 text-[11px] font-black border-2 ${formData.type === 'EXPENSE' ? 'bg-[#808080] text-white border-black' : 'bg-[#d4d0c8]'}`}>DEBIT (-)</button>
                  <button type="button" onClick={() => setFormData({...formData, type: 'INCOME'})} className={`py-2 text-[11px] font-black border-2 ${formData.type === 'INCOME' ? 'bg-[#808080] text-white border-black' : 'bg-[#d4d0c8]'}`}>CREDIT (+)</button>
                </div>
                <div className="space-y-1"><label className="text-xs font-black uppercase">Description:</label><input type="text" required className="w-full px-3 py-2 bg-white border-2 border-gray-500 border-r-white border-b-white outline-none font-black" value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} /></div>
                {!isSplitMode ? (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <label className="text-xs font-black uppercase">Store_ID:</label>
                      <select className="w-full px-2 py-2 bg-white border-2 border-gray-500 border-r-white border-b-white outline-none font-black text-xs" value={formData.toko} onChange={(e) => setFormData({...formData, toko: e.target.value})}>
                        {stores.map((t, idx) => t && t.name ? <option key={`opt-main-${t.name}-${idx}`} value={t.name}>{t.name}</option> : null)}
                      </select>
                    </div>
                    <div className="space-y-1"><label className="text-xs font-black uppercase">Val_Entry:</label><input type="text" required className="w-full px-3 py-2 bg-white border-2 border-gray-500 border-r-white border-b-white outline-none font-black text-blue-900" placeholder="0.00" value={formData.amount} onChange={(e) => setFormData({...formData, amount: e.target.value})} /></div>
                  </div>
                ) : (
                  <div className="bg-[#c0c0c0] p-3 border-2 border-[#808080] space-y-4 shadow-inner">
                    <button type="button" onClick={handleAutoSplit} className="w-full py-2 bg-[#d4d0c8] border-2 border-white border-r-black border-b-black text-[10px] font-black uppercase flex items-center justify-center gap-2 hover:bg-white"><Zap size={14} fill="currentColor"/> Split_To_Main_Brands</button>
                    <div className="grid grid-cols-2 gap-2"><input type="text" placeholder="TOT_VAL" className="px-2 py-2 border-2 border-gray-500 border-r-white border-b-white" value={splitData.totalAmount} onChange={(e) => setSplitData({...splitData, totalAmount: e.target.value})} /><input type="number" placeholder="QTY" className="px-2 py-2 border-2 border-gray-500 border-r-white border-b-white" value={splitData.totalItems} onChange={(e) => setSplitData({...splitData, totalItems: e.target.value})} /></div>
                    <div className="space-y-1 max-h-[150px] overflow-y-auto pr-1">
                      {splitData.rows.map((row, idx) => (
                        <div key={`split-row-${idx}`} className="flex gap-1 items-center bg-[#d4d0c8] p-1 border border-black/20">
                          <select className="flex-1 text-[10px] font-black" value={row.toko} onChange={(e) => { const nr = [...splitData.rows]; nr[idx].toko = e.target.value; setSplitData({...splitData, rows: nr}); }}>
                            {stores.map((t, sidx) => t && t.name ? <option key={`split-opt-v-${t.name}-${sidx}`} value={t.name}>{t.name}</option> : null)}
                          </select>
                          <input type="number" className="w-12 text-center text-[11px] font-black border border-gray-400" value={row.itemCount} onChange={(e) => { const nr = [...splitData.rows]; nr[idx].itemCount = e.target.value; setSplitData({...splitData, rows: nr}); }} />
                          <button type="button" onClick={() => { const nr = [...splitData.rows]; nr.splice(idx, 1); setSplitData({...splitData, rows: nr}); }} className="text-red-700 font-bold px-2 text-sm hover:bg-white">X</button>
                        </div>
                      ))}
                    </div>
                    <button type="button" onClick={() => setSplitData({...splitData, rows: [...splitData.rows, { toko: stores[0]?.name || '', itemCount: '' }]})} className="w-full py-1 bg-[#d4d0c8] border-2 border-white border-r-black border-b-black text-[11px] font-black">+ ADD_ROW</button>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1"><label className="text-xs font-black uppercase">Division:</label><select className="w-full px-2 py-2 bg-white border-2 border-gray-500 border-r-white border-b-white outline-none font-black text-xs" value={formData.division} onChange={(e) => setFormData({...formData, division: e.target.value})}>{LIST_DIVISI.map(d => <option key={`div-opt-${d}`} value={d}>{d}</option>)}</select></div>
                  <div className="space-y-1"><label className="text-xs font-black uppercase">Source:</label><select className="w-full px-2 py-2 bg-white border-2 border-gray-500 border-r-white border-b-white outline-none font-black text-xs" value={formData.source} onChange={(e) => setFormData({...formData, source: e.target.value})}>{LIST_SUMBER_DANA.map(s => <option key={`src-opt-${s}`} value={s}>{s}</option>)}</select></div>
                </div>
                <div className="space-y-1"><label className="text-xs font-black uppercase">Notes:</label><textarea rows="2" className="w-full px-3 py-2 bg-white border-2 border-gray-500 border-r-white border-b-white outline-none font-black text-xs resize-none" value={formData.notes} onChange={(e) => setFormData({...formData, notes: e.target.value})} placeholder="..." /></div>
                <button type="submit" className="w-full bg-[#d4d0c8] border-2 border-white border-r-black border-b-black font-black py-3 text-sm active:border-inset uppercase tracking-wider hover:bg-white transition-all shadow-md">COMMIT_RECORD</button>
              </form>
            </div>
          </div>

          {/* Database Grid Win98 */}
          <div className="lg:col-span-8 space-y-6 font-mono">
            <div className="bg-[#d4d0c8] border-2 border-white border-r-black border-b-black overflow-hidden shadow-md">
              <div className="p-4 border-b-2 border-[#808080] bg-[#c0c0c0] space-y-5">
                <div className="flex flex-col sm:flex-row justify-between gap-3">
                  <h2 className="text-base font-black flex items-center gap-2 tracking-tighter underline uppercase"><Filter size={18} /> Master_Ledger</h2>
                  <div className="flex gap-2">
                    <button onClick={handleCopySelected} disabled={selectedIds.length === 0} className={`bg-[#d4d0c8] border-2 border-white border-r-black border-b-black px-4 py-2 text-[11px] font-black flex items-center gap-2 ${selectedIds.length > 0 ? 'hover:bg-white active:shadow-inner' : 'opacity-50 grayscale'}`}><Copy size={16}/> COPY_SELECTED ({selectedIds.length})</button>
                    <button onClick={exportToCSV} className="bg-[#d4d0c8] border-2 border-white border-r-black border-b-black px-4 py-2 text-[11px] font-black flex items-center gap-2 hover:bg-white"><Download size={16}/> CSV_EXPORT</button>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="relative"><Search className="absolute left-2 top-2.5 text-[#808080]" size={16} /><input type="text" placeholder="Search entries..." className="w-full bg-white border-2 border-gray-500 border-r-white border-b-white pl-10 py-2 text-xs font-black outline-none shadow-inner focus:bg-yellow-50" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} /></div>
                  
                  {/* RESTORED FILTERS: Year, Month, Category, Store */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 border-t border-[#808080] pt-4">
                      <div className="space-y-1">
                        <label className="text-[10px] font-black uppercase text-[#404040]">YEAR:</label>
                        <select className="w-full bg-white border-2 border-gray-500 border-r-white border-b-white text-[10px] font-black px-1 py-1" value={filterYear} onChange={(e) => setFilterYear(e.target.value)}>
                          <option value="ALL">--- ALL_YEARS ---</option>
                          {[2024, 2025, 2026, 2027].map(y => <option key={`y-${y}`} value={y}>{y}</option>)}
                        </select>
                      </div>
                      <div className="space-y-1">
                        <label className="text-[10px] font-black uppercase text-[#404040]">MONTH:</label>
                        <select className="w-full bg-white border-2 border-gray-500 border-r-white border-b-white text-[10px] font-black px-1 py-1" value={filterMonth} onChange={(e) => setFilterMonth(e.target.value)}>
                          {MONTHS.map(m => <option key={`flt-m-${m.val}`} value={m.val}>{m.label}</option>)}
                        </select>
                      </div>
                      <div className="space-y-1">
                        <label className="text-[10px] font-black uppercase text-[#404040]">CATEGORY:</label>
                        <select className="w-full bg-white border-2 border-gray-500 border-r-white border-b-white text-[10px] font-black px-1 py-1" value={filterSource} onChange={(e) => setFilterSource(e.target.value)}>
                          <option value="SEMUA">--- ALL_DEPT ---</option>
                          {LIST_SUMBER_DANA.map(s => <option key={`flt-s-${s}`} value={s}>{s}</option>)}
                        </select>
                      </div>
                      <div className="space-y-1">
                        <label className="text-[10px] font-black uppercase text-[#404040]">STORE:</label>
                        <select className="w-full bg-white border-2 border-gray-500 border-r-white border-b-white text-[10px] font-black px-1 py-1" value={filterToko} onChange={(e) => setFilterToko(e.target.value)}>
                          <option value="SEMUA">--- ALL_STORES ---</option>
                          {stores.map((t, idx) => t && t.name ? <option key={`flt-t-${t.name}-${idx}`} value={t.name}>{t.name}</option> : null)}
                        </select>
                      </div>
                  </div>
                </div>
              </div>

              <div className="overflow-x-auto custom-scrollbar">
                <table className="w-full text-left border-collapse bg-white">
                  <thead>
                    <tr className="bg-[#d4d0c8] text-[11px] font-black border-b-2 border-[#808080]">
                      <th className="px-4 py-2 border-r border-[#808080] w-12 text-center"><button onClick={handleToggleSelectAll}>{isAllSelected ? <CheckSquare size={18} fill="#000080" className="text-white" /> : <Square size={18} />}</button></th>
                      <th className="px-4 py-2 border-r border-[#808080]">DATABASE_DETAILS</th>
                      <th className="px-4 py-2 border-r border-[#808080] text-center w-28">STORE</th>
                      <th className="px-4 py-2 border-r border-[#808080] text-right w-40">VALUATION</th>
                      <th className="px-4 py-2 text-center w-40">OPS</th>
                    </tr>
                  </thead>
                  <tbody className="text-xs font-bold">
                    {filteredList.length === 0 ? (
                      <tr><td colSpan="5" className="p-12 text-center bg-gray-50 italic uppercase text-gray-500 underline decoration-red-700 decoration-wavy">[ ZERO_MATCHES_RETURNED ]</td></tr>
                    ) : (
                      filteredList.slice(0, visibleCount).map((item) => (
                        <tr key={`tx-row-${item.id}`} className="border-b border-[#c0c0c0] hover:bg-[#ffffcc]">
                          <td className="px-2 py-2 border-r border-[#c0c0c0] text-center"><button onClick={() => toggleSelect(item.id)}>{selectedIds.includes(item.id) ? <CheckSquare size={18} fill="#000080" className="text-white" /> : <Square size={18} />}</button></td>
                          <td className="px-4 py-2 border-r border-[#c0c0c0]"><div className="flex gap-2"><span className={`font-black text-sm ${item.type === 'INCOME' ? 'text-green-700' : 'text-red-700'}`}>{item.type === 'INCOME' ? '[+]' : '[-]'}</span><div><p className="font-black text-sm text-gray-800 tracking-tighter uppercase">{item.description}</p>{item.notes && <div className="text-[11px] text-blue-900 bg-blue-50/50 p-2 border border-dotted border-blue-400 mt-1 flex gap-1 shadow-inner"><FileText size={12} className="shrink-0 mt-0.5" /><p className="font-black italic">Notes: {item.notes}</p></div>}<p className="text-[10px] text-gray-400 font-black mt-1 uppercase tracking-tight">{item.createdAt?.seconds ? new Date(item.createdAt.seconds * 1000).toLocaleString('id-ID') : 'SYNCING...'}</p></div></div></td>
                          <td className="px-4 py-2 border-r border-[#c0c0c0] text-center"><div className="flex flex-col gap-1 items-center"><span className="px-2 py-0.5 bg-[#d4d0c8] border border-black font-black text-[10px] shadow-sm w-full">{String(item.toko || 'N/A')}</span><span className="text-[10px] font-black text-blue-900 tracking-tighter uppercase">{item.division}</span><span className="text-[9px] bg-gray-100 border border-[#c0c0c0] px-1 font-black underline">F:{item.source}</span></div></td>
                          <td className={`px-4 py-2 border-r border-[#c0c0c0] text-right font-black text-sm ${item.type === 'INCOME' ? 'text-green-700' : 'text-red-700'}`}>{safeFormatIDR(item.amount).replace('Rp', '')}</td>
                          <td className="px-4 py-2 text-center"><div className="flex flex-col gap-1"><select value={item.status} onChange={(e) => updateStatus(item.id, e.target.value)} className={`text-[10px] font-black text-center py-1 outline-none border-2 border-inset shadow-sm ${getStatusColor(item.status)}`}>{LIST_STATUS.map(s => <option key={`st-opt-${s}`} value={s}>{s}</option>)}</select><div className="grid grid-cols-2 gap-1"><button onClick={() => { setEditItem({...item, amount: String(item.amount)}); setIsEditModalOpen(true); }} className="bg-[#d4d0c8] border border-black text-[10px] font-black py-1 hover:bg-white active:shadow-inner">EDIT</button><button onClick={() => setConfirmDeleteId(item.id)} className="bg-[#d4d0c8] border border-black text-[10px] font-black text-red-800 py-1 hover:bg-red-50 active:shadow-inner">DEL</button></div></div></td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              {filteredList.length > visibleCount && (<div className="p-4 bg-[#d4d0c8] flex justify-center border-t-2 border-[#808080]"><button onClick={() => setVisibleCount(v => v + ITEMS_PER_PAGE)} className="px-16 py-2 bg-[#d4d0c8] border-2 border-white border-r-black border-b-black text-sm font-black hover:bg-white uppercase tracking-widest active:shadow-inner">RETRIEVE_MORE_DATA_BLOCKS</button></div>)}
            </div>

            <div className="bg-[#d4d0c8] border-2 border-white border-r-black border-b-black p-6 shadow-md"><h2 className="text-base font-black mb-6 flex items-center gap-2 text-[#000080] border-b-2 border-[#000080] pb-2 underline uppercase tracking-wide decoration-double underline-offset-4"><ArrowRightLeft size={20}/> FINANCIAL_MATRIX_AUDIT</h2><div className="grid grid-cols-1 md:grid-cols-2 gap-8"><div className="p-4 border-2 border-[#808080] shadow-inner bg-white/40"><h3 className="text-[11px] font-black mb-4 uppercase bg-[#000080] text-white px-3 py-2 shadow-md tracking-widest underline underline-offset-4">RESERVE_SEO</h3><div className="grid grid-cols-1 sm:grid-cols-2 gap-2">{Object.entries(tokoSummary).map(([name, d]) => d && (<div key={`sum-seo-${name}`} className="p-2 bg-white border border-[#c0c0c0] flex justify-between text-[11px] font-black shadow-sm rounded-md transition-all hover:bg-yellow-50"><span>{name}</span><span className={Number(d.balanceSEO) >= 0 ? 'text-green-700' : 'text-red-700'}>{Number(d.balanceSEO).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span></div>))}</div></div><div className="p-4 border-2 border-[#808080] shadow-inner bg-white/40"><h3 className="text-[11px] font-black mb-4 uppercase bg-[#400040] text-white px-3 py-2 shadow-md tracking-widest underline underline-offset-4">RESERVE_HRD</h3><div className="grid grid-cols-1 sm:grid-cols-2 gap-2">{Object.entries(tokoSummary).map(([name, d]) => d && (<div key={`sum-hrd-${name}`} className="p-2 bg-white border border-[#c0c0c0] flex justify-between text-[11px] font-black shadow-sm rounded-md transition-all hover:bg-yellow-50"><span>{name}</span><span className={Number(d.balanceHRD) >= 0 ? 'text-green-700' : 'text-red-700'}>{Number(d.balanceHRD).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span></div>))}</div></div></div></div>
          </div>
        </div>
      </div>

      {/* --- VB DIALOGS (MODALS) --- */}

      {/* Store Management Modal */}
      {isStoreMgmtModalOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-[#d4d0c8] border-2 border-white border-r-black border-b-black shadow-2xl w-full max-w-md p-1 animate-in zoom-in-95 duration-150">
            <div className="bg-[#000080] text-white p-2 px-3 flex items-center justify-between font-bold text-sm mb-4 uppercase">
              <span>STORE_DATABASE_MANAGER</span>
              <button onClick={() => setIsStoreMgmtModalOpen(false)} className="w-6 h-6 bg-[#d4d0c8] text-black border border-black flex items-center justify-center font-bold text-xs hover:bg-red-600 transition-colors">X</button>
            </div>
            <div className="p-5 space-y-6">
              <div className="space-y-2">
                <label className="text-xs font-black uppercase underline decoration-dotted">Register_Store:</label>
                <div className="flex flex-col gap-2 font-mono">
                  <div className="flex gap-2">
                    <input type="text" placeholder="NAME..." id="newStoreName" className="flex-1 bg-white border-2 border-gray-500 border-r-white border-b-white px-3 py-2 outline-none font-black text-sm" />
                    <select id="newStoreCat" className="bg-white border-2 border-gray-500 border-r-white border-b-white px-2 py-1 font-black text-xs">
                      <option value="MAIN">MAIN BRAND</option>
                      <option value="POCKET">POCKET</option>
                    </select>
                  </div>
                  <button onClick={() => { 
                    const name = document.getElementById('newStoreName').value;
                    const cat = document.getElementById('newStoreCat').value;
                    if(name) { handleAddStore(name, cat); document.getElementById('newStoreName').value = ''; }
                  }} className="bg-[#d4d0c8] border-2 border-white border-r-black border-b-black font-black text-xs py-2 hover:bg-white active:shadow-inner uppercase">ADD_TO_DATABASE</button>
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-xs font-black uppercase underline decoration-dotted font-mono">Managed_Records:</label>
                <div className="max-h-[250px] overflow-y-auto bg-white border-2 border-gray-500 border-r-white border-b-white p-2 space-y-1">
                  {stores.map((s, idx) => s && s.name && (
                    <div key={`store-list-${s.name}-${idx}`} className="flex items-center justify-between p-2 border-b border-dotted border-[#c0c0c0] hover:bg-yellow-50 group font-mono">
                      {storeToEdit?.name === s.name ? (
                        <div className="flex flex-1 gap-2">
                          <input autoFocus className="flex-1 font-black text-sm outline-none bg-blue-50 px-1" defaultValue={s.name} onBlur={(e) => handleUpdateStore(s.name, e.target.value, storeToEdit.category)} onKeyDown={(e) => { if(e.key === 'Enter') handleUpdateStore(s.name, e.target.value, storeToEdit.category); if(e.key === 'Escape') setStoreToEdit(null); }} />
                          <select className="bg-blue-50 font-black text-[10px]" value={storeToEdit.category} onChange={(e) => setStoreToEdit({...storeToEdit, category: e.target.value})}>
                            <option value="MAIN">MAIN</option>
                            <option value="POCKET">POCKET</option>
                          </select>
                        </div>
                      ) : (
                        <>
                          <div className="flex flex-col">
                            <span className="font-black text-sm">{s.name}</span>
                            <span className={`text-[9px] font-black px-1 w-fit ${s.category === 'MAIN' ? 'bg-blue-800 text-white' : 'bg-gray-400 text-black'}`}>{s.category === 'MAIN' ? 'MAIN BRAND' : 'POCKET'}</span>
                          </div>
                          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button onClick={() => setStoreToEdit(s)} className="p-1 hover:bg-[#d4d0c8] border border-[#808080]"><Edit3 size={12}/></button>
                            <button onClick={() => handleDeleteStore(s.name)} className="p-1 hover:bg-red-50 border border-red-700 text-red-800"><Trash2 size={12}/></button>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Account Settings Modal */}
      {isAccountModalOpen && accountToEdit && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"><div className="bg-[#d4d0c8] border-2 border-white border-r-black border-b-black shadow-2xl w-full max-w-sm p-1 font-mono"><div className="bg-[#000080] text-white p-2 px-3 flex items-center justify-between font-bold text-sm mb-4 uppercase"><span>ACCOUNT_CONFIG: {accountToEdit}</span><button onClick={() => setIsAccountModalOpen(false)} className="w-6 h-6 bg-[#d4d0c8] text-black border border-black flex items-center justify-center font-bold text-xs hover:bg-red-600 transition-colors font-mono">X</button></div><form onSubmit={handleUpdateAccount} className="p-5 space-y-4"><div><label className="text-xs font-black uppercase tracking-tighter">NAME_ON_ACCOUNT:</label><input type="text" required className="w-full bg-white border-2 border-gray-500 border-r-white border-b-white px-4 py-2 outline-none text-sm font-black" value={accounts[accountToEdit].owner} onChange={(e) => setAccounts({...accounts, [accountToEdit]: {...accounts[accountToEdit], owner: e.target.value.toUpperCase()}})} /></div><div><label className="text-xs font-black uppercase tracking-tighter">BANK_INSTITUTION:</label><input type="text" required className="w-full bg-white border-2 border-gray-500 border-r-white border-b-white px-4 py-2 outline-none text-sm font-black" value={accounts[accountToEdit].bank} onChange={(e) => setAccounts({...accounts, [accountToEdit]: {...accounts[accountToEdit], bank: e.target.value.toUpperCase()}})} /></div><div><label className="text-xs font-black uppercase tracking-tighter">ACCOUNT_NUM:</label><input type="text" required className="w-full bg-white border-2 border-gray-500 border-r-white border-b-white px-4 py-2 outline-none text-sm font-black tracking-widest" value={accounts[accountToEdit].number} onChange={(e) => setAccounts({...accounts, [accountToEdit]: {...accounts[accountToEdit], number: e.target.value}})} /></div><div className="flex justify-end gap-3 mt-8 border-t-2 border-[#808080] pt-5"><button type="button" onClick={() => setIsAccountModalOpen(false)} className="px-8 py-2 bg-[#d4d0c8] border-2 border-white font-black text-xs active:shadow-inner">CANCEL</button><button type="submit" className="px-12 py-2 bg-[#d4d0c8] border-2 border-white border-r-black border-b-black font-black text-xs hover:bg-white text-blue-900 shadow-sm uppercase tracking-widest">COMMIT_SAVE</button></div></form></div></div>
      )}

      {/* Edit Transaction Modal */}
      {isEditModalOpen && editItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"><div className="bg-[#d4d0c8] border-2 border-white border-r-black border-b-black shadow-2xl w-full max-w-xl p-1 animate-in zoom-in-95 duration-150 font-mono"><div className="bg-[#000080] text-white p-2 px-3 flex items-center justify-between font-bold text-sm mb-4 uppercase"><span>DB_EDITOR: {editItem.id.substring(0, 12)}</span><button onClick={() => setIsEditModalOpen(false)} className="w-6 h-6 bg-[#d4d0c8] text-black border border-black flex items-center justify-center font-bold text-xs hover:bg-red-600 transition-colors font-mono">X</button></div><form onSubmit={handleUpdateTransaction} className="p-6 space-y-5 font-mono"><div className="grid grid-cols-2 gap-6"><div className="space-y-1"><label className="text-xs font-black uppercase underline tracking-tighter">Type:</label><select className="w-full bg-white border-2 border-gray-500 border-r-white border-b-white px-3 py-2 outline-none text-xs font-black" value={editItem.type} onChange={(e) => setEditItem({...editItem, type: e.target.value})}><option value="EXPENSE">DEBIT (-)</option><option value="INCOME">CREDIT (+)</option></select></div><div className="space-y-1"><label className="text-xs font-black uppercase underline tracking-tighter">Valuation:</label><input type="text" required className="w-full bg-white border-2 border-gray-500 border-r-white border-b-white px-4 py-2 outline-none text-sm font-black text-blue-900" value={editItem.amount} onChange={(e) => setEditItem({...editItem, amount: e.target.value})} /></div></div><div className="space-y-1"><label className="text-xs font-black uppercase underline">Description:</label><input type="text" required className="w-full bg-white border-2 border-gray-500 border-r-white border-b-white px-4 py-2 outline-none text-sm font-black" value={editItem.description} onChange={(e) => setEditItem({...editItem, description: e.target.value})} /></div><div className="grid grid-cols-2 gap-6"><div className="space-y-1"><label className="text-xs font-black uppercase underline">Store:</label><select className="w-full bg-white border-2 border-gray-500 border-r-white border-b-white px-3 py-2 outline-none text-xs font-black" value={editItem.toko} onChange={(e) => setEditItem({...editItem, toko: e.target.value})}>{stores.map((t, idx) => t && t.name ? <option key={`edit-store-${t.name}-${idx}`} value={t.name}>{t.name}</option> : null)}</select></div><div className="space-y-1"><label className="text-xs font-black uppercase underline">Fund:</label><select className="w-full bg-white border-2 border-gray-500 border-r-white border-b-white px-3 py-2 outline-none text-xs font-black" value={editItem.source} onChange={(e) => setEditItem({...editItem, source: e.target.value})}>{LIST_SUMBER_DANA.map(s => <option key={`edit-fund-${s}`} value={s}>{s}</option>)}</select></div></div><div className="space-y-1"><label className="text-xs font-black uppercase underline">Notes:</label><textarea rows="4" className="w-full bg-white border-2 border-gray-500 border-r-white border-b-white px-4 py-3 outline-none text-xs font-black resize-none" value={editItem.notes || ''} onChange={(e) => setEditItem({...editItem, notes: e.target.value})} /></div><div className="flex justify-end gap-4 mt-8 border-t-2 border-[#808080] pt-6"><button type="button" onClick={() => setIsEditModalOpen(false)} className="px-10 py-3 bg-[#d4d0c8] border-2 border-white font-black text-xs uppercase active:shadow-inner">DISCARD</button><button type="submit" className="px-12 py-3 bg-[#d4d0c8] border-2 border-white border-r-black border-b-black font-black text-xs hover:bg-white text-blue-900 border-blue-900/10 shadow-sm">COMMIT_UPDATE</button></div></form></div></div>
      )}

      {confirmDeleteId && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 backdrop-blur-sm"><div className="bg-[#d4d0c8] border-2 border-white border-r-black border-b-black shadow-2xl w-full max-w-xs p-1 animate-in zoom-in-95 duration-100 font-mono"><div className="bg-[#000080] text-white p-1 px-3 flex items-center justify-between font-bold text-sm mb-4 uppercase"><span>WARNING_SYSTEM</span><button onClick={() => setConfirmDeleteId(null)} className="text-black bg-[#d4d0c8] border border-black w-6 h-6 flex items-center justify-center font-bold font-mono">X</button></div><div className="p-6 flex flex-col items-center gap-6"><AlertTriangle size={56} className="text-red-700" /><p className="text-center font-black text-xs uppercase leading-relaxed text-red-800 underline decoration-wavy">PURGE THIS DATA ENTRY PERMANENTLY FROM DATABASE CLOUD?</p><div className="flex gap-4 w-full"><button onClick={executeDelete} className="flex-1 py-3 bg-[#d4d0c8] border-2 border-white border-r-black border-b-black font-black text-xs hover:bg-red-600 hover:text-white transition-colors uppercase">YES_PURGE</button><button onClick={() => setConfirmDeleteId(null)} className="flex-1 py-3 bg-[#d4d0c8] border-2 border-white border-r-black border-b-black font-black text-xs active:shadow-inner uppercase">NO_ABORT</button></div></div></div></div>
      )}

      {messageBox && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/30 backdrop-blur-[1px] font-mono"><div className="bg-[#d4d0c8] border-2 border-white border-r-black border-b-black shadow-2xl w-full max-w-xs p-1 animate-in slide-in-from-top-2"><div className={`p-1 px-4 flex items-center justify-between font-bold text-sm mb-4 ${messageBox.type === 'error' ? 'bg-red-800' : 'bg-[#000080]'} text-white`}><span>{String(messageBox.title)}</span><button onClick={() => setMessageBox(null)} className="text-black bg-[#d4d0c8] border border-black w-6 h-6 flex items-center justify-center font-bold font-mono">X</button></div><div className="p-6 flex flex-col items-center gap-6">{messageBox.type === 'error' ? <AlertTriangle size={40} className="text-red-700" /> : <Info size={40} className="text-blue-800" />}<p className="text-center font-black text-xs uppercase tracking-tight">{String(messageBox.msg)}</p><button onClick={() => setMessageBox(null)} className="w-24 py-2 bg-[#d4d0c8] border-2 border-white border-r-black border-b-black font-black text-xs mt-2 shadow-md active:shadow-inner">OK</button></div></div></div>
      )}
    </div>
  );
}